import React from 'react'
import moment from 'moment'

import Pager from './Pager'
import usePagination, { DEFAULT_PAGE_SIZE } from './usePagination'
import { heatLevel } from '../activityReport'

/**
 * Users x day intensity grid.
 *
 * Magnitude is the job here, so this is a sequential single-hue ramp rather than
 * categorical colour. Cells carry a `viz-heat--n` class instead of a computed
 * background, which keeps colour in the stylesheet and gives the ramp exactly
 * seven discrete steps.
 *
 * Counts live in the cell title rather than printed in the cell: at a week's
 * width the cells are too small for legible numerals, and the darker steps would
 * fail contrast against dark text.
 */
const ActivityHeatmap = ({ days, grid, peak, pageSize = DEFAULT_PAGE_SIZE }) => {
    // Rows are paged so the card stays a bounded height; the day columns still
    // scroll horizontally, which keeps the user column pinned beside them.
    const { pageItems, page, pageCount, setPage, start, total } = usePagination(grid, pageSize)

    return (
        <>
            <div className="viz-heatmap-scroll">
                <table className="viz-heatmap">
                    <thead>
                        <tr>
                            <th className="viz-heatmap-corner">User</th>
                            {days.map((day) => (
                                <th key={day} className="viz-heatmap-day" title={moment(day).format('dddd, MMM D YYYY')}>
                                    <span className="viz-heatmap-day-label">{moment(day).format('D')}</span>
                                    <span className="viz-heatmap-day-month">{moment(day).format('MMM')}</span>
                                </th>
                            ))}
                            <th className="viz-heatmap-total">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((row) => (
                            <tr key={row.userName}>
                                <th className="viz-heatmap-user" title={row.userName}>{row.userName}</th>
                                {row.cells.map((cell) => (
                                    <td
                                        key={cell.day}
                                        className={`viz-heat viz-heat--${heatLevel(cell.count, peak)}`}
                                        title={`${row.userName} — ${moment(cell.day).format('MMM D')}: ${cell.count} action${cell.count === 1 ? '' : 's'}`}
                                    >
                                        <span className="viz-visually-hidden">{cell.count}</span>
                                    </td>
                                ))}
                                <td className="viz-heatmap-total">{row.total.toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="viz-heatmap-legend">
                    <span className="viz-heatmap-legend-label">Less</span>
                    {[0, 1, 2, 3, 4, 5, 6].map((level) => (
                        <span key={level} className={`viz-heat-key viz-heat--${level}`} />
                    ))}
                    <span className="viz-heatmap-legend-label">More</span>
                    <span className="viz-heatmap-legend-peak">Peak {peak.toLocaleString()} in a day</span>
                </div>
            </div>
            <Pager
                page={page}
                pageCount={pageCount}
                setPage={setPage}
                start={start}
                count={pageItems.length}
                total={total}
                unit="users"
            />
        </>
    )
}

export default ActivityHeatmap
